import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle } from "../../api/asyncData";
import {
	type AdminUser,
	getAdminDetail,
	getAdminUsers,
	sendReminder,
	setAdminDigest,
} from "../../api/commands/admin";
import { toMessage } from "../../api/errors";
import { i18next } from "../../i18n";
import { Dialog } from "../../widgets/dialog";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

export const FetchUsers = getAdminUsers.command(Message.CompletedFetchUsers);
export const FetchDetail = getAdminDetail.command(Message.CompletedFetchDetail);
export const SendReminder = sendReminder.command(Message.CompletedSendReminder);
export const SetDigest = setAdminDigest.command(Message.CompletedSetDigest);

/** Copies, then clears the "Copied" hint after two seconds. */
export const CopyEmail = Command.define("CopyAdminEmail", {
	args: { text: S.String },
	messages: [Message.CopyFeedbackExpired],
	execute: ({ text }) =>
		Effect.promise(() => navigator.clipboard.writeText(text)).pipe(
			Effect.catch(() => Effect.void),
			Effect.flatMap(() => Effect.sleep("2 seconds")),
			Effect.as(Message.CopyFeedbackExpired()),
		),
});

type Return = PageReturn<Model, Message>;

const dialogChild = {
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
};
const foldDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	...dialogChild,
});
const openDialog = Update.foldChildStep({
	update: Dialog.open,
	...dialogChild,
});
const closeDialog = Update.foldChildStep({
	update: Dialog.close,
	...dialogChild,
});

export const initial: Model = {
	page: "Admin",
	search: "",
	users: AsyncData.Idle(),
	selected: Option.none(),
	detail: AsyncData.Idle(),
	dialog: Dialog.init({ id: "confirm_reminder_dialog" }),
	sending: false,
	feedback: Option.none(),
	copied: false,
};

/** The selected user's row, when both the list and the selection exist. */
export const selectedUser = (model: Model): Option.Option<AdminUser> =>
	Option.flatMap(model.selected, (circleId) =>
		Option.fromNullishOr(
			AsyncData.getOrElse(model.users, () => null)?.data.find(
				(u) => u.circleId === circleId,
			),
		),
	);

/** Case-insensitive match on name, email, or circle. */
export const visibleUsers = (model: Model): ReadonlyArray<AdminUser> => {
	const q = model.search.trim().toLowerCase();
	const all = AsyncData.getOrElse(model.users, () => null)?.data ?? [];
	return q === ""
		? all
		: all.filter((u) =>
				`${u.name} ${u.email} ${u.circleName}`.toLowerCase().includes(q),
			);
};

const patchUser = (
	model: Model,
	email: string,
	patch: Partial<AdminUser>,
): Model =>
	evo(model, {
		users: (users) =>
			AsyncData.isSuccess(users)
				? AsyncData.succeed({
						data: users.data.data.map((u) =>
							u.email === email ? { ...u, ...patch } : u,
						),
					})
				: users,
	});

const feedback = (kind: "success" | "error", message: string) =>
	Option.some({ kind, message });

export const page: Page<Model, Message> = {
	init: (_route, shared) => ({
		model: evo(initial, { users: () => AsyncData.Loading() }),
		commands: [FetchUsers({ generation: shared.generation })],
	}),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchUsers: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { users: (u) => settle(u, result) }) },
			ChangedSearch: ({ search }) => ({
				model: evo(model, { search: () => search }),
			}),
			ClickedUser: ({ circleId }) =>
				Option.contains(model.selected, circleId)
					? { model }
					: {
							model: evo(model, {
								selected: () => Option.some(circleId),
								detail: () => AsyncData.Loading(),
								feedback: () => Option.none(),
							}),
							commands: [
								FetchDetail({ circleId, generation: shared.generation }),
							],
						},
			CompletedFetchDetail: ({ circleId, generation, result }) =>
				generation !== shared.generation ||
				!Option.contains(model.selected, circleId)
					? { model }
					: { model: evo(model, { detail: (d) => settle(d, result) }) },
			ClickedSendReminder: () => openDialog(model),
			ClickedConfirmSend: () =>
				Option.match(selectedUser(model), {
					onNone: () => ({ model }),
					onSome: (user) => ({
						model: evo(model, {
							sending: () => true,
							feedback: () => Option.none(),
						}),
						commands: [
							SendReminder({
								circleId: user.circleId,
								email: user.email,
								generation: shared.generation,
							}),
						],
					}),
				}),
			CompletedSendReminder: ({ email, generation, result }) => {
				if (generation !== shared.generation) return { model };
				const settled = evo(model, { sending: () => false });
				return Result.match(result, {
					onSuccess: ({ sent }) =>
						closeDialog(
							evo(settled, {
								feedback: () =>
									feedback(
										"success",
										sent
											? i18next.t("admin.sent", { email })
											: i18next.t("admin.nothingSent", { email }),
									),
							}),
						),
					onFailure: (error) =>
						closeDialog(
							evo(settled, {
								feedback: () => feedback("error", toMessage(error)),
							}),
						),
				});
			},
			ToggledDigest: ({ email, subscribed }) => ({
				model: evo(patchUser(model, email, { digestSubscribed: subscribed }), {
					feedback: () => Option.none(),
				}),
				commands: [
					SetDigest({ email, subscribed, generation: shared.generation }),
				],
			}),
			// Failure puts the switch back where it was.
			CompletedSetDigest: ({ email, subscribed, generation, result }) =>
				generation !== shared.generation || Result.isSuccess(result)
					? { model }
					: {
							model: evo(
								patchUser(model, email, { digestSubscribed: !subscribed }),
								{
									feedback: () => feedback("error", toMessage(result.failure)),
								},
							),
						},
			ClickedCopyEmail: () =>
				Option.match(selectedUser(model), {
					onNone: () => ({ model }),
					onSome: (user) => ({
						model: evo(model, { copied: () => true }),
						commands: [CopyEmail({ text: user.email })],
					}),
				}),
			CopyFeedbackExpired: () => ({
				model: evo(model, { copied: () => false }),
			}),
			GotDialogMessage: ({ message }) => foldDialog(model, message),
		}),
};
